import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "../../i18n";
import { Combobox, KeyValues, Notice, type ComboOption } from "../../design/components";
import { ApiError } from "../../api/client";
import { useAssignHold, useRoutingParticipants, type HeldRow } from "../../api/routing";
import { omr } from "../../lib/format";
import { useFocusTrap } from "../../lib/useFocusTrap";
import styles from "./inbound.module.css";

const MESSAGE_BY_CODE: Record<string, string> = {
  ALREADY_RESOLVED: "This document was already assigned",
  ENTITY_NOT_LIVE: "That company is not live yet.",
};

const MESSAGE_KEY_BY_CODE: Record<string, string> = {
  ALREADY_RESOLVED: "inbound.assignDialog.errorAlreadyResolved",
  ENTITY_NOT_LIVE: "inbound.assignDialog.errorEntityNotLive",
};

function failureMessage(error: unknown, t: (key: string, fallback: string) => string): string {
  const code = error instanceof ApiError ? error.code : "";
  const key = MESSAGE_KEY_BY_CODE[code];
  const fallback = MESSAGE_BY_CODE[code];
  if (key && fallback) return t(key, fallback);
  return t("inbound.assignDialog.errorGeneric", "The company could not be assigned.");
}

export function AssignCompanyDialog({
  hold,
  onClose,
  onAssigned,
}: {
  hold: HeldRow;
  onClose: () => void;
  onAssigned: (companyName: string) => void;
}) {
  // The suggestion fills the box by id; nothing is sent until the operator confirms.
  const [picked, setPicked] = useState<string | null>(hold.candidateEntityId);
  // Remounts the combobox after a pick so its input shows the name, not the id it commits.
  const [pickSeq, setPickSeq] = useState(0);
  const [query, setQuery] = useState("");
  const participants = useRoutingParticipants(query);
  const assign = useAssignHold();
  const box = useFocusTrap<HTMLDivElement>(onClose);
  const { t } = useTranslation();

  const rows = useMemo(() => participants.data ?? [], [participants.data]);
  const options = useMemo<ComboOption[]>(
    () =>
      rows.map((row) => ({
        v: row.entityId,
        n: row.legalName,
        d: `${row.entityCode} · CR ${row.crNumber} · ${row.participantId}`,
      })),
    [rows],
  );

  // The remount above drops focus, so it is put back on the input the operator was using.
  useEffect(() => {
    if (pickSeq === 0) return;
    box.current?.querySelector<HTMLInputElement>("#assign-company input")?.focus();
  }, [box, pickSeq]);

  const chosen = rows.find((row) => row.entityId === picked);
  const display = chosen?.legalName ?? (picked === hold.candidateEntityId ? hold.candidateEntityName ?? "" : "");

  const confirm = () => {
    if (!picked) return;
    const name = chosen?.legalName ?? hold.candidateEntityName ?? picked;
    assign.mutate({ holdId: hold.holdId, entityId: picked }, { onSuccess: () => onAssigned(name) });
  };

  return (
    <div className={styles.dialogWrap}>
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-label={t("inbound.assignDialog.ariaLabel", "Assign to company")}
        tabIndex={-1}
        ref={box}
      >
        <h2 className={styles.dialogTitle}>{t("inbound.assignDialog.title", "Assign to company")}</h2>
        <KeyValues
          pairs={[
            [
              t("inbound.assignDialog.receiverIdentifier", "Receiver identifier"),
              hold.receiverParticipant ?? t("inbound.assignDialog.noneOnDocument", "None on the document"),
            ],
            [t("inbound.assignDialog.supplier", "Supplier"), hold.sellerName ?? "—"],
            [t("inbound.assignDialog.total", "Total"), `${omr(hold.total)}${hold.currency ? ` ${hold.currency}` : ""}`],
          ]}
        />
        <div className={styles.dialogLabel}>{t("inbound.assignDialog.company", "Company")}</div>
        <Combobox
          key={pickSeq}
          id="assign-company"
          value={display}
          options={options}
          ariaLabel={t("inbound.assignDialog.company", "Company")}
          placeholder={t("inbound.assignDialog.searchCompanies", "Search companies…")}
          onQuery={setQuery}
          onPick={(entityId) => {
            setPicked(entityId);
            setPickSeq((seq) => seq + 1);
          }}
        />
        {assign.isError ? (
          <div className={styles.dialogNotice}>
            <Notice kind="fail">{failureMessage(assign.error, t)}</Notice>
          </div>
        ) : null}
        <div className={styles.dialogActions}>
          <button type="button" className="btn btn-sm btn-ghost" onClick={onClose}>
            {t("inbound.assignDialog.cancel", "Cancel")}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            disabled={!picked || assign.isPending}
            onClick={confirm}
          >
            {assign.isPending
              ? t("inbound.assignDialog.assigning", "Assigning…")
              : t("inbound.assignDialog.confirm", "Confirm assignment")}
          </button>
        </div>
      </div>
    </div>
  );
}
